/**
 * The audit trail, human-readable — globally and for one case.
 *
 * Two things on this screen are not decoration.
 *
 * **The verification banner renders the endpoint's `limitation` string
 * verbatim.** Not summarised, not shortened, not moved behind a tooltip. It is
 * the sentence saying that hash-linking detects accidental corruption, a partial
 * edit, a deleted row or a reordering — and *not* a rewrite by somebody with
 * database access, who can recompute every hash. A green tick with that sentence
 * removed is a claim this product cannot support, and in a proceeding the
 * overselling is the part that gets tested.
 *
 * **The export is the point of the export.** Verification against this
 * database's own head proves internal consistency, which is exactly what a
 * rewriter also achieves. A copy held somewhere this database cannot reach is
 * what makes the chain mean anything, so the CSV carries the hashes and the
 * limitation is the first line of the file.
 *
 * Every row's words come off the API. Nothing here composes a sentence about
 * what an act was.
 */

import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { AuditEntryView } from '../api/client'
import { ErrorEnvelope } from '../components/bits'

export function Audit() {
  return <AuditTrail />
}

export function CaseAudit() {
  const { caseId } = useParams()
  return <AuditTrail caseId={caseId} />
}

function AuditTrail({ caseId }: { caseId?: string }) {
  const page = useQuery({
    queryKey: ['audit', caseId ?? 'all'],
    queryFn: () => (caseId ? api.listCaseAudit(caseId) : api.listAudit()),
  })
  const verification = useQuery({ queryKey: ['audit-verify'], queryFn: () => api.verifyAudit() })

  return (
    <div className="page">
      <header>
        <div className="small muted">
          {caseId ? (
            <>
              <Link to={`/cases/${caseId}`}>Case</Link> ·{' '}
            </>
          ) : null}
          <Link to="/cases">Cases</Link>
        </div>
        <h1>Audit{caseId ? ' — this case' : ''}</h1>
        {/* **Pointing at a screen that is not there.** This said the case
            narrative "is the timeline on the case page" — and the timeline was
            hidden on 28 Aug 2026 by Archit's ruling, so the sentence sent an
            officer to look for something the product does not draw. The
            distinction it was making is real and worth keeping; what it names
            as the other half is now the case page itself. The card on the case
            page carried the same stale reference and went with the card. */}
        <p className="small muted">
          Who did what, to what, and when. Append-only and hash-linked. This is what happened to
          the system, not what happened in the proceeding — the case page is the second of those.
        </p>
      </header>

      <div className="card">
        <header>
          <h2>Verification</h2>
          <a className="btn" href={api.auditCsvUrl(caseId)} download data-testid="audit-csv">
            Export CSV
          </a>
        </header>
        <div className="body">
          {verification.isLoading ? <p className="muted">Recomputing the chain…</p> : null}
          {verification.error ? <ErrorEnvelope error={verification.error} /> : null}
          {verification.data ? (
            <>
              <p data-testid="audit-verdict">
                <span className={`chip ${verification.data.verified ? 'good' : 'bad'}`}>
                  {verification.data.verified ? 'Verified' : 'Broken'}
                </span>{' '}
                <span className="mono small">{verification.data.events} events</span>
                {verification.data.broken_at !== null &&
                verification.data.broken_at !== undefined ? (
                  <span className="warn-text"> — broken at seq {verification.data.broken_at}</span>
                ) : null}
                {verification.data.head ? (
                  <div className="small muted mono" style={{ marginTop: 4 }}>
                    head {verification.data.head}
                  </div>
                ) : null}
                {verification.data.detail ? (
                  <div className="reason">{verification.data.detail}</div>
                ) : null}
              </p>
              {/* Verbatim. Never summarised to tidy the screen. */}
              <p className="small muted" data-testid="audit-limitation">
                {verification.data.limitation}
              </p>
            </>
          ) : null}
        </div>
      </div>

      <div className="card">
        <header>
          <h2>The chain</h2>
          <span className="small muted">
            {page.data ? `${(page.data.entries ?? []).length} of ${page.data.total} shown` : ''}
          </span>
        </header>
        <div className="body">
          {page.isLoading ? <p className="muted">Reading the trail…</p> : null}
          {page.error ? <ErrorEnvelope error={page.error} /> : null}
          {page.data && (page.data.entries ?? []).length === 0 ? (
            <div className="empty">Nothing has been recorded yet.</div>
          ) : null}
          {page.data && (page.data.entries ?? []).length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="ledger-table" data-testid="audit-table">
                <thead>
                  <tr>
                    <th>Seq</th>
                    <th>At</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Subject</th>
                  </tr>
                </thead>
                <tbody>
                  {(page.data.entries ?? []).map((entry) => (
                    <Row key={entry.seq} entry={entry} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function Row({ entry }: { entry: AuditEntryView }) {
  return (
    <tr>
      <td className="mono">{entry.seq}</td>
      <td className="mono small">{new Date(entry.at).toLocaleString()}</td>
      <td>{entry.actor}</td>
      <td className="mono small">{entry.action}</td>
      <td className="mono small deva">{entry.subject}</td>
    </tr>
  )
}
